import { EApprovalStatus, SortDirection } from "./approval.model";
import { EReplyStatus } from "./recipients.model";
import { User } from "./user.model";

// The viewAnswers route takes an EApprovalStatus, which the view-answers header uses to decide
// between the approve/reject buttons and a read-only status badge. Reports is a tracking screen
// whose viewers (payroll/accounting/billing reps) are not necessarily the request's approvers, so a
// still-pending row maps to PendingPrevApproval - the one pending value that renders no action
// buttons - rather than to PendingApproval.
export function formStatusToApprovalStatus(status?: EReplyStatus): EApprovalStatus {
    switch (status) {
        case EReplyStatus.Rejected:
            return EApprovalStatus.Rejected;
        case EReplyStatus.PendingApproval:
            return EApprovalStatus.PendingPrevApproval;
        default:
            return EApprovalStatus.Approved;
    }
}

// One level of a row's approval chain. The server sends these in level order and flags the ones
// that already signed off, so the grid can highlight them individually - which a single pre-joined
// approversDisplay string could not.
export interface ReportApprover {
    id?: number;
    name: string;
    hasApproved: boolean;
}

export interface ReportRow {
    id: number;                    // Approval.Id of the final (end) approval level
    recipientGuid: string;         // for getExpenseRows(guid)
    recipientId: number;           // questionnaire number
    questionnarieName: string;
    createDate: Date;              // sent date
    sender: User;
    approvers: ReportApprover[];   // per-level, ordered; what the grid renders
    approversDisplay: string;      // "Dani Cohen, Ruti Levi" - the same names joined, for the tooltip
    mlmExportStatus: EnumExportStatus;
    // Latest successful payroll export; empty if never exported successfully (a Failed attempt
    // doesn't count).
    mlmLastExportDate?: Date;
    // Decision date of the end approval, only for an Approved form - empty while pending/rejected.
    formApprovalDate?: Date;
    // The request's own approval state, not the status of a single approval row - only
    // PendingApproval / Rejected / Approved ever arrive here (see ReportRowDto.FormStatus).
    formStatus: EReplyStatus;
    requiredAmount?: number;
    approvedAmount?: number;
    // Any expense line on this request that's flagged as client-charged (per-request aggregate of
    // ExpenseAnswer.ClientCharge, which is itself a per-line field).
    clientCharge: boolean;
}

export enum EnumExportStatus {
    Exported = 1,
    Failed = 2,
    NotExported = 3
}

export enum ReportSortBy {
    SentDate = 'sentDate',
    SenderName = 'senderName',
    RecipientId = 'recipientId',
    MlmLastExportDate = 'mlmLastExportDate',
    FormApprovalDate = 'formApprovalDate',
    Amount = 'amount'
}

export interface ReportSort {
    sortBy: ReportSortBy;
    sortDirection: SortDirection;
}
